import { isWithinInterval } from 'date-fns';
import { isNil, omitBy } from 'lodash-es';

import { FeatureConfig, FeatureFlagKey } from 'constants/dictionaries/featureFlags';

import { parseSeenGuides } from './seenGuides';
import type {
  ChangePassword,
  ChangePasswordDto,
  LoginByEmail,
  LoginByEmailDto,
  LoginByIdentifier,
  LoginByIdentifierDto,
  LoginResult,
  LoginResultDto,
  Me,
  MeDto,
  PasswordResetConfirm,
  PasswordResetConfirmDto,
  PasswordResetRequest,
  PasswordResetRequestDto,
  PermanentPasswordDto,
  PermanentPassword,
  Register,
  RegisterDto,
  SocialToken,
  SocialTokenDto,
  User,
  UserChoices,
  UserChoicesDto,
  UserDto,
} from './users.types';

type ParseMeForUI = (input: MeDto) => Me;
type ParseLoginByEmailForBE = (input: LoginByEmail) => LoginByEmailDto;
type ParseLoginByIdentifierForBE = (input: LoginByIdentifier) => LoginByIdentifierDto;
type ParseLoginResultForUI = (input: LoginResultDto) => LoginResult;
type ParsePermanentPasswordForBE = (input: PermanentPassword) => PermanentPasswordDto;
type ParseUserForUpdateBE = (input: Partial<User>) => Partial<UserDto>;
type ParsePartialMeForUI = (input: Partial<UserDto>) => Partial<User>;
type ParseUserChoicesForUI = (input: UserChoicesDto) => UserChoices;

const prepareFeatureConfig = (featureConfig: Partial<Record<FeatureFlagKey, string | boolean>> | null | undefined): FeatureConfig => {
  if (!featureConfig) return {};
  const result: FeatureConfig = {};
  Object.entries(featureConfig).forEach(([key, value]) => {
    // BE may send booleans; older payloads used string "true" / "false".
    result[key as FeatureFlagKey] = value === true || value === 'true';
  });
  return result;
};

const parseMeForUI: ParseMeForUI = data => {
  const {
    id,
    email,
    username,
    account_type,
    age_range,
    classroom,
    newsletter,
    origin,
    feature_config,
    has_full_access,
    is_verified,
    show_tutorial,
    has_password_set,
    warehouse_token,
    teacher,
    student,
    user_identifier,
    reply_email,
    assigned_class,
    assigned_classroom,
    assigned_school,
    data_processing_objection_sent_at,
    data_processing_objection_reason,
    config,
    subscriptions: rawSubscriptions,
    seen_guides,
  } = data;

  const assignedClassroom = assigned_classroom ?? assigned_class ?? null;

  const subscriptions = rawSubscriptions
    ? rawSubscriptions.map(subscription => ({
        validFrom: new Date(subscription.valid_from),
        validUntil: new Date(subscription.valid_until),
        accessTypeName: subscription.access_type_name,
      }))
    : [];

  const hasPremiumAccess = () => {
    if (has_full_access) return true;
    return (
      !!subscriptions?.length &&
      subscriptions.some(subscription => isWithinInterval(new Date(), { start: subscription.validFrom, end: subscription.validUntil }))
    );
  };

  return {
    id,
    email,
    username,
    accountType: account_type,
    ageRange: age_range ?? '',
    classroom: classroom ?? '',
    newsletter: Boolean(newsletter),
    origin: origin ?? null,
    featureConfig: prepareFeatureConfig(feature_config),
    hasFullAccess: has_full_access,
    hasPremiumAccess: hasPremiumAccess(),
    hasPasswordSet: has_password_set,
    isVerified: is_verified,
    showTutorial: show_tutorial,
    subscriptions,
    student,
    teacher,
    isStudent: !isNil(student),
    isTeacher: !isNil(teacher),
    isSchoolAccount: !isNil(student) || !isNil(teacher),
    warehouseToken: warehouse_token,
    userIdentifier: user_identifier,
    replyEmail: reply_email,
    dataProcessingObjectionReason: data_processing_objection_reason,
    dataProcessingObjectionSentAt: data_processing_objection_sent_at ? new Date(data_processing_objection_sent_at) : null,
    config: {
      useSecondaryEmail: Boolean(config?.use_secondary_email),
    },
    assignedClass: assignedClassroom
      ? {
          id: assignedClassroom.id,
          name: assignedClassroom.name,
          letter: assignedClassroom.letter,
          level: assignedClassroom.level,
        }
      : null,
    assignedSchool: assigned_school
      ? {
          address: assigned_school.address,
          city: assigned_school.city,
          district: assigned_school.district,
          id: assigned_school.id,
          name: assigned_school.name,
          shortName: assigned_school.short_name,
        }
      : null,
    seenGuides: parseSeenGuides(seen_guides),
  };
};

const parseLoginByEmailForBE: ParseLoginByEmailForBE = data => data;
const parseLoginByIdentifierForBE: ParseLoginByIdentifierForBE = data => {
  const { loginIdentifier, ...rest } = data;
  return {
    ...rest,
    login_identifier: loginIdentifier,
  };
};
const parseLoginResultForUI: ParseLoginResultForUI = data => ({
  access: data.access,
  refresh: data.refresh,
  isNewUser: Boolean(data.is_new_user),
});

const parsePermanentPasswordForBE: ParsePermanentPasswordForBE = data => ({
  password1: data.password1,
  password2: data.password2,
});

const parseMeForUpdateBE: ParseUserForUpdateBE = formData => {
  const notNullableValues = omitBy(
    {
      account_type: formData.accountType,
      age_range: formData.ageRange,
      classroom: formData.classroom,
      newsletter: formData.newsletter,
      origin: formData.origin,
      show_tutorial: formData.showTutorial,
      username: formData.username,
    },
    e => isNil(e) || e === '',
  );

  const nullableValues = {
    data_processing_objection_reason: formData.dataProcessingObjectionReason,
    reply_email: formData.replyEmail,
  };

  return { ...notNullableValues, ...nullableValues };
};

const parsePartialMeForUI: ParsePartialMeForUI = rawData => {
  const notNullableValues = omitBy(
    {
      accountType: rawData.account_type,
      ageRange: rawData.age_range,
      classroom: rawData.classroom,
      newsletter: rawData.newsletter,
      origin: rawData.origin,
      showTutorial: rawData.show_tutorial,
      username: rawData.username,
    },
    e => isNil(e) || e === '',
  );

  const nullableValues = {
    dataProcessingObjectionReason: rawData.data_processing_objection_reason || null,
    dataProcessingObjectionSentAt: rawData.data_processing_objection_sent_at ? new Date(rawData.data_processing_objection_sent_at) : null,
    replyEmail: rawData.reply_email,
  };

  return { ...notNullableValues, ...nullableValues };
};

const parseUserChoicesForUI: ParseUserChoicesForUI = data => data ?? {};

const parseRegisterForBE = (data: Register): RegisterDto => ({
  username: data.username,
  email: data.email,
  password: data.password,
  repeat_password: data.repeatPassword,
  recaptcha: data.recaptcha,
  source: data.source,
  source_id: data.sourceId,
});

const parseSocialTokenForBE = (data: SocialToken): SocialTokenDto => ({
  provider: data.provider,
  access_token: data.accessToken,
});

const parsePasswordResetRequestForBE = (data: PasswordResetRequest): PasswordResetRequestDto => ({
  login_identifier: data.loginIdentifier,
  redirect_url: data.redirectUrl,
});

const parsePasswordResetConfirmForBE = (data: PasswordResetConfirm): PasswordResetConfirmDto => ({
  uidb64: data.uidb64,
  token: data.token,
  password: data.password,
});

const parseChangePasswordForBE = (data: ChangePassword): ChangePasswordDto => ({
  old_password: data.oldPassword,
  new_password: data.newPassword,
  repeat_new_password: data.repeatNewPassword,
});

const flattenFieldErrors = (data: unknown): string => {
  if (!data || typeof data !== 'object') return 'Wystąpił nieznany błąd';
  const messages = Object.values(data as Record<string, unknown>).flatMap(value => {
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
    if (typeof value === 'string') return [value];
    return [];
  });
  return messages.length ? messages.join('\n') : 'Wystąpił nieznany błąd';
};

export {
  parseMeForUI,
  parseLoginByEmailForBE,
  parseLoginResultForUI,
  parseLoginByIdentifierForBE,
  parsePermanentPasswordForBE,
  parseMeForUpdateBE,
  parsePartialMeForUI,
  parseUserChoicesForUI,
  parseRegisterForBE,
  parseSocialTokenForBE,
  parsePasswordResetRequestForBE,
  parsePasswordResetConfirmForBE,
  parseChangePasswordForBE,
  flattenFieldErrors,
};
